import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Home, LogIn } from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import catesWhiteLogo from '../../assets/cates_logo_mark_white.png';
import { APP_CONFIG } from '../../config/app.config';

const DEMO_ACCOUNTS = [
  { label: 'Admin', email: 'admin@catesroofing.com', role: 'admin' },
  { label: 'Customer', email: 'sarah@example.com', role: 'customer' },
  { label: 'Contractor', email: 'marcus@example.com', role: 'contractor' },
];

const BRAND_BLUE = '#0072c6';
const BRAND_LIGHT = '#1493e6';
const BRAND_SILVER = '#e6edf5';
const BRAND_DARK = '#050608';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAppStore();
  const navigate = useNavigate();
  const panelText = 'white';
  const panelMuted = BRAND_SILVER;
  const cardBg = 'rgba(255,255,255,0.96)';
  const cardBorder = 'rgba(0,114,198,0.32)';

  const handleLogin = (e: React.FormEvent, overrideEmail?: string) => {
    e.preventDefault();
    const target = overrideEmail || email;
    const ok = login(target);
    if (ok) {
      const { user } = useAppStore.getState();
      const dest = user?.role === 'admin' ? '/admin/dashboard' : user?.role === 'contractor' ? '/contractor/dashboard' : '/portal/dashboard';
      navigate(dest);
    } else {
      setError('Invalid credentials. Try a demo account below.');
    }
  };

  return (
    <div className="login-shell" style={{ height: '100dvh', display: 'flex', background: BRAND_SILVER, position: 'relative', overflow: 'hidden' }}>
      <Link
        to="/"
        aria-label="Return to home page"
        title="Home"
        style={{
          position: 'absolute', top: '1.5rem', right: '1.75rem', zIndex: 5,
          color: BRAND_BLUE, display: 'flex', textDecoration: 'none',
          transition: 'color 0.2s ease, transform 0.2s ease',
        }}
        onMouseEnter={e => { e.currentTarget.style.color = BRAND_LIGHT; e.currentTarget.style.transform = 'scale(1.1)'; }}
        onMouseLeave={e => { e.currentTarget.style.color = BRAND_BLUE; e.currentTarget.style.transform = 'scale(1)'; }}
      >
        <Home size={24} strokeWidth={2.2} />
      </Link>
      <div className="login-diagonal" aria-hidden="true" />
      {/* Left Panel */}
      <div style={{ flex: '0 0 480px', background: BRAND_DARK, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '3rem', position: 'relative', overflow: 'hidden', borderRight: `1px solid ${BRAND_BLUE}` }} className="login-panel">
        <div style={{ position: 'absolute', top: '10%', right: '-18%', width: '420px', height: '420px', borderRadius: '50%', background: 'radial-gradient(circle, rgb(0 114 198 / 0.28), transparent 68%)', pointerEvents: 'none' }} />
        <Link to="/" className="login-logo" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', width: '12rem', height: '4rem', marginBottom: '3rem', transform: 'translateX(-2.5rem)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '176px' }}>
            <img
              src={catesWhiteLogo}
              alt="Cates Roofing & Contracting"
              style={{ height: '54px', width: '176px', objectFit: 'contain', display: 'block' }}
              onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            />
            <span className="nav-strapline" style={{
              display: 'block', marginTop: '0.25rem',
              color: 'rgba(255,255,255,0.82)', fontSize: '0.5rem',
              fontWeight: 300, letterSpacing: '0.06em', textTransform: 'uppercase',
              whiteSpace: 'nowrap', lineHeight: 1, textAlign: 'center',
            }}>
              – Family Legacy {APP_CONFIG.familyBusinessLegacySince} –
            </span>
          </div>
        </Link>
        <h1 style={{ color: panelText, fontWeight: 900, fontSize: '2.35rem', lineHeight: 1.08, margin: '0 0 1.25rem', textTransform: 'uppercase' }}>
          Welcome Back to Your Portal
        </h1>
        <p style={{ color: panelMuted, lineHeight: 1.7, marginBottom: '2.5rem', maxWidth: '360px' }}>
          Access your projects, quotes, appointments, and invoices — all in one place.
        </p>
        {['Track your project in real-time', 'Review and approve quotes online', 'Manage appointments & invoices', 'Upload documents securely'].map(item => (
          <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.875rem' }}>
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: BRAND_LIGHT, flexShrink: 0 }} />
            <span style={{ color: panelMuted, fontSize: '0.9rem', fontWeight: 600 }}>{item}</span>
          </div>
        ))}
      </div>

      {/* Right Panel */}
      <div className="login-form-panel" style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem 2rem', background: `linear-gradient(135deg, ${BRAND_SILVER} 0%, white 55%, rgba(20,147,230,0.18) 100%)`, overflow: 'hidden' }}>
        <div className="auth-light" style={{ width: '100%', maxWidth: '460px', background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: '1rem', padding: '1.5rem 2rem', boxShadow: '0 24px 70px rgba(5,6,8,0.12)' }}>
          <div style={{ height: '4px', background: `linear-gradient(90deg, ${BRAND_BLUE}, ${BRAND_LIGHT})`, borderRadius: '9999px', marginBottom: '1.5rem' }} />
          <h2 style={{ fontWeight: 900, fontSize: '1.85rem', color: BRAND_DARK, marginBottom: '0.5rem', textTransform: 'uppercase' }}>Sign In</h2>
          <p style={{ color: BRAND_DARK, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            Don't have an account? <Link to="/register" style={{ color: BRAND_BLUE, fontWeight: 800 }}>Register here</Link>
          </p>

          {/* Demo Accounts */}
          <div style={{ background: BRAND_SILVER, borderRadius: '0.75rem', padding: '1rem', marginBottom: '1.5rem', border: `1px solid ${cardBorder}` }}>
            <p style={{ fontSize: '0.75rem', fontWeight: 700, color: BRAND_BLUE, marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Demo Accounts</p>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {DEMO_ACCOUNTS.map(acc => (
                <button key={acc.role} onClick={e => { setEmail(acc.email); handleLogin(e, acc.email); }} style={{
                  padding: '0.45rem 0.9rem', borderRadius: '0.5rem', border: `1px solid ${acc.role === 'customer' ? cardBorder : 'transparent'}`, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 800, transition: 'opacity 0.2s',
                  background: acc.role === 'customer' ? 'white' : BRAND_BLUE,
                  color: acc.role === 'customer' ? BRAND_BLUE : 'white',
                }}>
                  {acc.label}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleLogin}>
            {error && (
              <div style={{ background: BRAND_SILVER, color: BRAND_BLUE, padding: '0.75rem 1rem', borderRadius: '0.625rem', fontSize: '0.85rem', marginBottom: '1.25rem', fontWeight: 700 }}>
                {error}
              </div>
            )}
            <div style={{ marginBottom: '1rem' }}>
              <label className="label">Email Address</label>
              <input className="input-field" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="label">Password</label>
              <div style={{ position: 'relative' }}>
                <input className="input-field" type={show ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" style={{ paddingRight: '2.75rem' }} />
                <button type="button" onClick={() => setShow(!show)} style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <div style={{ textAlign: 'right', marginTop: '0.375rem' }}>
                <Link to="/forgot-password" style={{ fontSize: '0.8rem', color: '#1493e6' }}>Forgot password?</Link>
              </div>
            </div>
            <button type="submit" className="btn-primary" style={{ width: '100%' }}>
              <LogIn size={16} /> Sign In
            </button>
          </form>

          <p style={{ textAlign: 'center', color: BRAND_DARK, fontSize: '0.8rem', margin: '1.25rem 0 0' }}>
            Need emergency help? <a href="tel:5558472663" style={{ color: '#1493e6', fontWeight: 600 }}>Call 24/7</a>
          </p>
        </div>
      </div>
      <style>{`
        .login-diagonal {
          position: absolute;
          left: 454px;
          top: 0;
          bottom: 0;
          width: 72px;
          background: linear-gradient(180deg, #0072c6, #1493e6);
          clip-path: polygon(36% 0, 100% 0, 64% 100%, 0 100%);
          z-index: 1;
          pointer-events: none;
        }
        .login-panel, .auth-light { position: relative; z-index: 2; }
        .login-shell { color-scheme: light; }
        .auth-light .label { color: #050608; }
        .auth-light .input-field {
          background: #ffffff !important;
          color: #050608 !important;
          border-color: rgba(0,114,198,0.45) !important;
        }
        .auth-light .input-field::placeholder {
          color: rgba(0,114,198,0.72) !important;
          opacity: 1;
        }
        @media(max-height: 820px) {
          .auth-light { padding-top: 1rem !important; padding-bottom: 1rem !important; }
          .auth-light > div:first-child { margin-bottom: 0.8rem !important; }
          .auth-light h2 { margin-top: 0 !important; }
          .login-logo { margin-bottom: 1.75rem !important; }
        }
        @media(max-width:768px){
          .login-panel,.login-diagonal{display:none!important}
          .login-form-panel{padding:1rem!important}
          .auth-light{padding:1.25rem!important}
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
